import React, { createContext, useContext, useState, useMemo } from 'react';
import { INITIAL_INVOICES, MONTHLY_DATA, INITIAL_SALES_INVOICES } from '@/data/mockData';

const AppContext = createContext(null);

// Mar 2026 is the current month base
const CURRENT_MONTH = MONTHLY_DATA[MONTHLY_DATA.length - 1];

export function AppProvider({ children }) {
  const [currentScreen, setCurrentScreen] = useState('HOME');
  const [screenParams, setScreenParams] = useState({});
  const [history, setHistory] = useState([]);
  const [activeTab, setActiveTab] = useState('HOME');
  const [invoices, setInvoices] = useState(INITIAL_INVOICES);
  // Sales invoices state
  const [salesInvoices, setSalesInvoices] = useState(INITIAL_SALES_INVOICES);
  // Track additional uploaded amount from user actions
  const [additionalUploaded, setAdditionalUploaded] = useState(0);

  const navigate = (screen, params = {}) => {
    setHistory(prev => [...prev, { screen: currentScreen, params: screenParams }]);
    setCurrentScreen(screen);
    setScreenParams(params);
    if (['HOME', 'PRODUCTS', 'INVOICES', 'PROFILE'].includes(screen)) {
      setActiveTab(screen);
    }
    const el = document.querySelector('.screen-content');
    if (el) el.scrollTop = 0;
  };

  const goBack = () => {
    if (history.length > 0) {
      const prev = history[history.length - 1];
      setHistory(h => h.slice(0, -1));
      setCurrentScreen(prev.screen);
      setScreenParams(prev.params);
      const el = document.querySelector('.screen-content');
      if (el) el.scrollTop = 0;
    }
  };

  const updateInvoiceStatus = (invoiceId, newStatus) => {
    const inv = INITIAL_INVOICES.find(i => i.id === invoiceId);
    if (inv && ['PENDING_UPLOAD', 'OCR_CONFIRMATION'].includes(inv.status) &&
        ['UNDER_REVIEW', 'VERIFIED'].includes(newStatus)) {
      setAdditionalUploaded(prev => prev + inv.amount);
    }
    setInvoices(prev =>
      prev.map(i => i.id === invoiceId ? { ...i, status: newStatus } : i)
    );
  };

  // Add new sales invoice
  const addSalesInvoice = (invoice) => {
    const newInvoice = {
      ...invoice,
      id: Date.now(),
      status: 'UNDER_REVIEW',
      createdAt: new Date().toISOString(),
    };
    setSalesInvoices(prev => [newInvoice, ...prev]);
  };

  // Update sales invoice status
  const updateSalesInvoiceStatus = (invoiceId, newStatus) => {
    setSalesInvoices(prev =>
      prev.map(i => i.id === invoiceId ? { ...i, status: newStatus } : i)
    );
  };

  const pendingInvoices = useMemo(
    () => invoices.filter(inv => ['PENDING_UPLOAD', 'OCR_CONFIRMATION'].includes(inv.status)),
    [invoices]
  );

  const pendingCount = pendingInvoices.length;
  // Use base monthly values + user-driven updates
  const uploadedValue = CURRENT_MONTH.uploaded + additionalUploaded;
  const pendingValue = Math.max(0, CURRENT_MONTH.pending - additionalUploaded);
  const completionPct = Math.min(100, Math.round((uploadedValue / CURRENT_MONTH.sale) * 100));

  return (
    <AppContext.Provider value={{
      currentScreen, screenParams, activeTab,
      navigate, goBack,
      invoices, updateInvoiceStatus,
      salesInvoices, addSalesInvoice, updateSalesInvoiceStatus,
      pendingCount, pendingValue, uploadedValue, completionPct,
    }}>
      {children}
    </AppContext.Provider>
  );
}

export const useApp = () => useContext(AppContext);
